// import { useMutation } from "@tanstack/react-query";
import React, {
  createContext,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { apiOperador } from "../services/api";
import { useCookies } from "react-cookie";
import jwtDecode from "jwt-decode";

interface OperadorCaixaContextProviderProps {
  children: ReactNode;
}

interface OperadorCaixaContextProps {
  page: number;
  tab: string;
  loading: boolean;
  setTab: (value: string) => void;
  setPage: (page: number | SetStateAction<number>) => void;
  loadData: (filters: FilterProps) => Promise<Data | undefined>;
  loadVendedores: () => Promise<void>;
  loadAssessorias: () => Promise<void>;
  clearData(): void
  filtros: FilterProps;
  setFiltros: (filtros: FilterProps) => void;
  data: Data | null;
  vendedores: VendedorProps[];
  assessorias: AssessoriasProps[];
}

export interface VendedorProps {
  nome_vendedor: string;
}

export interface AssessoriasProps {
  assessoria: string;
}

export interface Data {
  contratos: number;
  a_cobrar: number;
  valor_vencido: number;
  valor_a_vencer: number;
  data: DataItem[];
}

export interface DataItem {
  id_lista_loja: number;
  loja: number;
  cpf_cnpj: string;
  nome_cliente: string;
  nome_vendedor: string;
  atraso: number;
  valor_vencido: number;
  valor_a_vencer: number;
  assessoria: string;
  data_ultimo_acionamento: string;
  data_agendamento: string;
  id_processo: string;
  processo: string;
  fone1?: string;
  fone2?: string;
  fone3?: string;
  fone4?: string;
  fone5?: string;
  fone6?: string;
  fone7?: string;
  fone8?: string;
  email?: string;
  data_lista?: string;
}

interface FilterProps {
  page: number;
  loja?: number;
  assessoria?: string;
  de?: number;
  ate?: number;
  valor_inicial?: number;
  valor_final?: number;
  vendedor?: string;
  data?: string;
  agendamentos?: string;
  acionamentos?: string;
  processos?: string;
  shouldNotRefresh?: boolean;
}

const OperadorCaixaContext = createContext<OperadorCaixaContextProps>(
  {} as OperadorCaixaContextProps
);

export const OperadorCaixaProvider = ({
  children,
}: OperadorCaixaContextProviderProps) => {
  const [tab, setTab] = useState("fila-cobranca");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vendedores, setVendedores] = useState<VendedorProps[]>([]);
  const [assessorias, setAssessorias] = useState<AssessoriasProps[]>([]);
  const [data, setData] = useState<Data | null>(null);
  const [cookies] = useCookies(["token"]);
  const [filtros, setFiltros] = useState<FilterProps>({
    page
  });

  async function loadData(params: FilterProps | null) {
    if (!params?.shouldNotRefresh) setLoading(true);
    try {
      const { token } = cookies;
      const decoded = jwtDecode(token) as any;
      // console.log(decoded)
      const { data } = await apiOperador.get<{
        data: Data;
      }>("https://malta-ws.gazin.com.br:3000/operador-cadastro/operador-caixa", {
        params: {
          cpf_cnpj: decoded.cpf_cnpj,
          loja: decoded.id_loja,
          ...params,
        },
      });
      setData(data.data);
      return data.data;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }



  async function loadVendedores() {
    const { token } = cookies;
    const decoded = jwtDecode(token) as any;
    const { data } = await apiOperador.get("/vendedor", {
      params: {
        loja: decoded.id_loja,
      },
    });
    const vendedores = data.vendedores.filter(
      (value: { nome_vendedor: string }) => value.nome_vendedor !== null
    );
    setVendedores(
      vendedores.sort(
        (
          a: {
            nome_vendedor: string;
          },
          b: {
            nome_vendedor: string;
          }
        ) =>
          a.nome_vendedor
            .toLowerCase()
            .localeCompare(b.nome_vendedor.toLowerCase())
      )
    );
  }

  async function loadAssessorias() {
    const { token } = cookies;
    const decoded = jwtDecode(token) as any;
    const { data } = await apiOperador.get("/assessorias", {
      params: {
        loja: decoded.id_loja,
      },
    });
    const assessorias = data.assessorias.filter(
      (value: { assessoria: string }) => value.assessoria !== null
    );
    setAssessorias(
      assessorias.sort(
        (
          a: {
            assessoria: string;
          },
          b: {
            assessoria: string;
          }
        ) =>
          a.assessoria.toLowerCase().localeCompare(b.assessoria.toLowerCase())
      )
    );
  }
  useEffect(() => {
    loadData(filtros);
    loadVendedores();
    loadAssessorias();
  }, []);

  function clearData() {
    setVendedores([])
    setAssessorias([])
    setFiltros({
      page: 0
    })
    setPage(0)
    setData(null)
  }

  return (
    <OperadorCaixaContext.Provider
      value={{
        page,
        tab,
        loading,
        setTab,
        setPage,
        filtros,
        setFiltros,
        vendedores,
        assessorias,
        data,
        loadData,
        loadVendedores,
        loadAssessorias,
        clearData
      }}
    >
      {children}
    </OperadorCaixaContext.Provider>
  );
};

export const useOperadorCaixa = () => useContext(OperadorCaixaContext);
